/*
 * Vial scent quiz. Reads the product list that sections/vial-scent-quiz.liquid prints as
 * JSON, asks six questions, scores every in-stock decant on its tags, and offers a set
 * that goes into the cart in one request. The add dispatches the same cart event as the
 * theme's own product form, so the cart drawer opens and refreshes as usual.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = document.querySelector('[data-vial-quiz]');

if (root && !root.dataset.ready) {
  root.dataset.ready = 'true';
  init(root);
}

function init(root) {
  const app = root.querySelector('[data-vial-quiz-app]');
  const head = root.querySelector('[data-vial-quiz-head]');
  const dataEl = root.querySelector('[data-vial-quiz-data]');
  const PICKS = clamp(parseInt(root.dataset.picks, 10) || 5, 3, 6);
  const FREE_SHIP = parseInt(root.dataset.freeShip, 10) || 6000;
  const GIFT = parseInt(root.dataset.gift, 10) || 0;
  const GIFT_URL = root.dataset.giftUrl || '';
  const FALLBACK_URL = root.dataset.fallbackUrl || '/collections/all';

  let pool = [];
  try {
    pool = JSON.parse(dataEl.textContent).filter((p) => p.v && p.v.some((v) => v.a));
  } catch (e) {
    app.innerHTML = `<p class="vial-quiz__sub">The quiz couldn't load. <a href="${FALLBACK_URL}">Browse every fragrance</a> instead.</p>`;
    return;
  }
  pool.forEach((p) => {
    p.tagset = new Set((p.tags || []).map((t) => String(t).toLowerCase()));
  });

  // Scent families, each mapped to the store's own product tags.
  const FAM = {
    fresh: ['Fresh & citrus', 'Bergamot, sea air, green tea', ['fresh', 'citrus', 'aquatic', 'marine', 'tea', 'green', 'clean', 'mint']],
    fruity: ['Fruity', 'Pineapple, mango, cherry, fig', ['fruity', 'tropical', 'mango', 'pineapple', 'apple', 'cherry', 'fig']],
    sweet: ['Sweet & gourmand', 'Vanilla, coffee, honey, tonka', ['sweet', 'vanilla', 'gourmand', 'coffee', 'honey', 'toffee', 'tonka', 'chestnut', 'coconut', 'rum']],
    woody: ['Woody', 'Cedar, sandalwood, vetiver', ['woody']],
    amber: ['Warm & amber', 'Amber, resin, cosy spice', ['amber', 'warm', 'oriental', 'resinous']],
    smoky: ['Oud, smoke & leather', 'Oud, incense, tobacco', ['oud', 'incense', 'smoky', 'tobacco', 'leather']],
    spicy: ['Spicy', 'Pepper, cardamom, ginger', ['spicy', 'ginger']],
    floral: ['Floral & powdery', 'Rose, iris, soft musk', ['floral', 'rose', 'iris', 'powdery', 'musk']],
    aromatic: ['Aromatic & herbal', 'Lavender, sage, mint', ['aromatic', 'lavender', 'mint']],
  };

  const WHEN_TAGS = {
    everyday: ['office', 'versatile', 'clean', 'fresh', 'citrus', 'tea', 'aromatic'],
    evening: ['date night', 'warm', 'amber', 'vanilla', 'oud', 'tobacco', 'leather', 'sweet'],
    warm: ['summer', 'aquatic', 'marine', 'citrus', 'tropical', 'fresh'],
    cool: ['winter', 'warm', 'vanilla', 'amber', 'tobacco', 'gourmand', 'oud', 'coffee', 'spicy'],
  };

  const Q = [
    {
      id: 'style',
      title: 'Who is it for?',
      sub: 'Most of our range is unisex, so every option has plenty to choose from.',
      type: 'one',
      opts: [
        ['him', 'Him', 'Masculine and unisex scents'],
        ['her', 'Her', 'Unisex scents with a softer side'],
        ['neutral', 'Gender-neutral', 'Unisex scents only'],
        ['any', 'Show me everything', 'No preference'],
      ],
    },
    {
      id: 'love',
      title: 'Which scents do you love?',
      sub: 'Pick up to 3.',
      type: 'multi',
      min: 1,
      max: 3,
      opts: Object.keys(FAM).map((k) => [k, FAM[k][0], FAM[k][1]]),
    },
    {
      id: 'avoid',
      title: 'Anything you’d rather skip?',
      sub: 'Optional. We’ll leave these out.',
      type: 'multi',
      min: 0,
      max: 6,
      opts: [
        ['smoky', 'Oud, smoke & leather', 'Incense, tobacco, leather'],
        ['sweet', 'Very sweet', 'Vanilla-heavy, dessert-like'],
        ['floral', 'Florals', 'Rose, iris, powdery'],
        ['spicy', 'Spicy', 'Pepper, cardamom, ginger'],
        ['aquatic', 'Aquatic', 'Sea spray, marine notes'],
        ['none', 'No dislikes', 'I’m open to anything'],
      ],
    },
    {
      id: 'when',
      title: 'When will you wear it most?',
      sub: 'Pick up to 2.',
      type: 'multi',
      min: 1,
      max: 2,
      opts: [
        ['everyday', 'Everyday & work', 'Easy, polished'],
        ['evening', 'Evenings & dates', 'Richer, more alluring'],
        ['warm', 'Warm weather', 'Spring and summer'],
        ['cool', 'Cool weather', 'Autumn and winter'],
      ],
    },
    {
      id: 'strength',
      title: 'How strong do you like it?',
      sub: 'How far it carries, not how long it lasts.',
      type: 'one',
      opts: [
        ['soft', 'Soft & close', 'A skin scent'],
        ['moderate', 'Noticeable', 'A light trail'],
        ['strong', 'Beast mode', 'Fills the room'],
      ],
    },
    {
      id: 'budget',
      title: 'What price range suits you?',
      sub: 'Based on the 2ml. You can switch sizes on your results.',
      type: 'one',
      opts: [
        ['value', 'Under $8 a 2ml', 'Designer and Middle Eastern favourites'],
        ['any', 'Mix it up', 'Best match at any price'],
        ['niche', 'Niche & luxury', '$10 and up a 2ml'],
      ],
    },
  ];

  const LABEL = {
    everyday: 'Everyday',
    evening: 'Evenings',
    warm: 'Warm weather',
    cool: 'Cool weather',
    soft: 'Soft',
    moderate: 'Noticeable',
    strong: 'Strong',
  };

  let A;
  let step;
  let picks = [];
  const size = {};
  const off = {};

  function reset() {
    A = { style: null, love: [], avoid: [], when: [], strength: null, budget: null };
    step = 0;
    picks = [];
    for (const k of Object.keys(size)) delete size[k];
    for (const k of Object.keys(off)) delete off[k];
  }

  reset();
  renderQ();

  // ---------- scoring ----------

  function has(p, list) {
    return list.some((t) => p.tagset.has(t));
  }

  function famScore(p, f) {
    const n = FAM[f][2].filter((t) => p.tagset.has(t)).length;
    return Math.min(n, 2) / 2;
  }

  function projection(p) {
    const m = Number(p.m && p.m.proj);
    if (m >= 1 && m <= 5) return m;
    if (has(p, ['powerhouse', 'intense', 'extrait'])) return 4.5;
    if (has(p, ['oud', 'tobacco', 'leather', 'smoky', 'amber'])) return 3.9;
    if (has(p, ['fresh', 'citrus', 'tea', 'clean', 'office', 'aquatic'])) return 2.8;
    return 3.3;
  }

  function lower(v) {
    if (Array.isArray(v)) return v.map((x) => String(x).toLowerCase());
    if (v == null) return [];
    return String(v).toLowerCase().split(/[,/]| and /).map((x) => x.trim());
  }

  function whenMatch(p, w) {
    const seasons = lower(p.m && p.m.seasons);
    const occ = lower(p.m && p.m.occ);
    if (w === 'warm' && seasons.length) return seasons.some((s) => /spring|summer|year|all/.test(s));
    if (w === 'cool' && seasons.length) return seasons.some((s) => /autumn|fall|winter|year|all/.test(s));
    if (w === 'everyday' && occ.length && occ.some((o) => /every|day|office|work|casual|versatile/.test(o))) return true;
    if (w === 'evening' && occ.length && occ.some((o) => /even|night|date/.test(o))) return true;
    return has(p, WHEN_TAGS[w]);
  }

  function twoMl(p) {
    return p.v.find((v) => /^2\s*ml$/i.test(v.s)) || p.v[0];
  }

  function score(p, relaxed) {
    const tags = p.tagset;
    if (A.style === 'him' && !(tags.has('mens') || tags.has('unisex'))) return null;
    if ((A.style === 'her' || A.style === 'neutral') && !tags.has('unisex')) return null;

    const small = twoMl(p);
    if (!small || !small.a) return null;
    const price = small.p / 100;
    if (!relaxed) {
      if (A.budget === 'value' && price > 8) return null;
      if (A.budget === 'niche' && price < 10) return null;
    }

    let pen = 0;
    for (const a of A.avoid) {
      if (a === 'none') continue;
      const s = a === 'aquatic' ? (has(p, ['aquatic', 'marine']) ? 1 : 0) : famScore(p, a);
      if (s >= 1 && !relaxed) return null;
      if (s >= 0.5) pen += 14;
    }

    let fam = 0;
    const why = [];
    for (const f of A.love) {
      const s = famScore(p, f);
      fam += s;
      if (s >= 0.5) why.push(FAM[f][0]);
    }
    if (fam === 0) return null;

    let s = 40 * Math.min(fam, 2) / 2 - pen;
    if (A.style === 'him' && tags.has('mens')) s += 5;
    if (A.style === 'her' && has(p, ['floral', 'fruity', 'sweet', 'vanilla', 'musk', 'powdery'])) s += 6;

    for (const w of A.when) {
      if (whenMatch(p, w)) {
        s += 8;
        why.push(LABEL[w]);
      }
    }

    const target = { soft: 2.5, moderate: 3.5, strong: 4.6 }[A.strength || 'moderate'];
    s -= 7 * Math.abs(projection(p) - target);

    if (p.pop || tags.has('bestseller')) s += 5;
    if (tags.has('just-landed')) s += 2;

    return { p, s, why: [...new Set(why)].slice(0, 3) };
  }

  function recommend() {
    let scored = pool.map((p) => score(p, false)).filter(Boolean);
    let relaxed = false;
    if (scored.length < PICKS) {
      relaxed = true;
      scored = pool.map((p) => score(p, true)).filter(Boolean);
    }
    scored.sort((a, b) => b.s - a.s);

    const out = [];
    const houses = {};
    const maxPerHouse = scored.length > PICKS * 3 ? 1 : 2;
    const fams = A.love.slice();
    let i = 0;
    let guard = 0;
    while (out.length < PICKS && guard++ < 60) {
      const f = fams[i++ % fams.length];
      let cand = scored.find((x) => !out.includes(x) && (houses[x.p.b] || 0) < maxPerHouse && famScore(x.p, f) >= 0.5);
      if (!cand) cand = scored.find((x) => !out.includes(x) && (houses[x.p.b] || 0) < maxPerHouse);
      if (!cand) cand = scored.find((x) => !out.includes(x));
      if (!cand) break;
      out.push(cand);
      houses[cand.p.b] = (houses[cand.p.b] || 0) + 1;
    }
    out.sort((a, b) => b.s - a.s);
    return { picks: out, relaxed };
  }

  // ---------- rendering ----------

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function money(cents) {
    const d = cents / 100;
    return '$' + (Number.isInteger(d) ? d.toString() : d.toFixed(2));
  }

  function valid(q) {
    return q.type === 'one' ? !!A[q.id] : A[q.id].length >= q.min;
  }

  function renderQ() {
    const q = Q[step];
    const isOn = (v) => (q.type === 'one' ? A[q.id] === v : A[q.id].includes(v));
    const cols = q.opts.length === 4 ? 'vial-quiz__opts--2' : q.opts.length === 6 || q.opts.length === 9 ? 'vial-quiz__opts--3' : '';
    app.innerHTML = `
      <div class="vial-quiz__progress" aria-hidden="true">
        ${Q.map((_, i) => `<span class="${i < step ? 'is-done' : i === step ? 'is-current' : ''}"></span>`).join('')}
      </div>
      <p class="vial-quiz__count">Question ${step + 1} of ${Q.length}</p>
      <h2 class="vial-quiz__q" tabindex="-1">${esc(q.title)}</h2>
      <p class="vial-quiz__hint">${esc(q.sub)}</p>
      <div class="vial-quiz__opts ${cols}" role="group" aria-label="${esc(q.title)}">
        ${q.opts
          .map(
            (o) => `<button type="button" class="vial-quiz__opt" data-v="${o[0]}" aria-pressed="${isOn(o[0])}">
              <span class="vial-quiz__opt-title">${esc(o[1])}</span>
              <span class="vial-quiz__opt-sub">${esc(o[2])}</span>
            </button>`
          )
          .join('')}
      </div>
      <div class="vial-quiz__nav">
        <button type="button" class="button button-secondary vial-quiz__back" ${step === 0 ? 'hidden' : ''}>Back</button>
        <button type="button" class="button vial-quiz__next" ${valid(q) ? '' : 'disabled'}>${step === Q.length - 1 ? 'See my matches' : 'Next'}</button>
      </div>`;

    app.querySelectorAll('.vial-quiz__opt').forEach((b) =>
      b.addEventListener('click', () => {
        const v = b.dataset.v;
        if (q.type === 'one') {
          A[q.id] = v;
        } else {
          let a = A[q.id];
          if (v === 'none') a = a.includes('none') ? [] : ['none'];
          else {
            a = a.filter((x) => x !== 'none');
            a = a.includes(v) ? a.filter((x) => x !== v) : a.length < q.max ? a.concat(v) : a;
          }
          A[q.id] = a;
        }
        renderQ();
        app.querySelector(`.vial-quiz__opt[data-v="${v}"]`)?.focus();
        if (q.type === 'one') setTimeout(next, 180);
      })
    );
    app.querySelector('.vial-quiz__back').addEventListener('click', () => {
      step--;
      renderQ();
      focusQ();
    });
    app.querySelector('.vial-quiz__next').addEventListener('click', next);
  }

  function next() {
    if (!valid(Q[step])) return;
    if (step < Q.length - 1) {
      step++;
      renderQ();
      focusQ();
    } else {
      showResults();
    }
  }

  function focusQ() {
    const h = app.querySelector('.vial-quiz__q, .vial-quiz__rtitle');
    if (h) h.focus({ preventScroll: true });
    const top = root.getBoundingClientRect().top + window.scrollY - 90;
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
  }

  function chosen(x) {
    const id = size[x.p.h];
    return x.p.v.find((v) => v.id === id) || twoMl(x.p);
  }

  function totals() {
    const inSet = picks.filter((x) => !off[x.p.h]);
    return { inSet, total: inSet.reduce((t, x) => t + chosen(x).p, 0) };
  }

  function summaryText(total, count) {
    if (!count) return 'Tick at least one to add your set.';
    const parts = [`${count} decant${count === 1 ? '' : 's'}: <strong>${money(total)}</strong>.`];
    if (total < FREE_SHIP) parts.push(`Add ${money(FREE_SHIP - total)} for free shipping.`);
    else if (GIFT && total < GIFT) parts.push(`Free shipping unlocked. ${money(GIFT - total)} more for a <a href="${GIFT_URL}">free 2ml</a>.`);
    else if (GIFT) parts.push(`Free shipping and a <a href="${GIFT_URL}">free 2ml</a> unlocked.`);
    else parts.push('Free shipping unlocked.');
    return parts.join(' ');
  }

  // "Roja Elysium Pour Homme" from Roja Parfums reads as "Elysium Pour Homme" under the house line.
  function shortName(p) {
    const words = [p.b].concat(String(p.b).split(/\s+/).filter((w) => w.length > 2));
    for (const w of words) {
      const re = new RegExp('^' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i');
      if (re.test(p.t)) return p.t.replace(re, '');
    }
    return p.t;
  }

  function card(x) {
    const p = x.p;
    const cur = chosen(x);
    const isOff = !!off[p.h];
    const sizes = p.v
      .map(
        (v) => `<button type="button" class="vial-quiz__size" data-h="${esc(p.h)}" data-id="${v.id}" aria-pressed="${v.id === cur.id}" ${v.a ? '' : 'disabled'}>
          <span>${esc(v.s)}</span><span>${money(v.p)}</span>
        </button>`
      )
      .join('');
    return `<li class="vial-quiz__card${isOff ? ' is-off' : ''}">
      <a class="vial-quiz__media" href="${esc(p.u)}">
        ${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.t)} bottle" width="400" height="400" loading="lazy">` : ''}
      </a>
      <div class="vial-quiz__body">
        <p class="vial-quiz__house">${esc(p.b)}</p>
        <h3 class="vial-quiz__name"><a href="${esc(p.u)}">${esc(shortName(p))}</a></h3>
        ${x.why.length ? `<p class="vial-quiz__why">${x.why.map(esc).join(' · ')}</p>` : ''}
        <div class="vial-quiz__sizes" role="group" aria-label="Size for ${esc(p.t)}">${sizes}</div>
        <label class="vial-quiz__keep">
          <input type="checkbox" data-keep="${esc(p.h)}" ${isOff ? '' : 'checked'}>
          Include in my set
        </label>
      </div>
    </li>`;
  }

  function showResults() {
    const r = recommend();
    picks = r.picks;
    head.hidden = true;

    if (!picks.length) {
      app.innerHTML = `
        <h2 class="vial-quiz__rtitle" tabindex="-1">No close matches right now</h2>
        <p class="vial-quiz__hint">Try picking more scent families or fewer dislikes, or <a href="${FALLBACK_URL}">browse every fragrance</a>.</p>
        <div class="vial-quiz__nav"><button type="button" class="button vial-quiz__restart">Start again</button></div>`;
      app.querySelector('.vial-quiz__restart').addEventListener('click', restart);
      focusQ();
      return;
    }

    const { inSet, total } = totals();
    app.innerHTML = `
      <div class="vial-quiz__rhead">
        <p class="vial-quiz__eyebrow">Your matches</p>
        <h2 class="vial-quiz__rtitle" tabindex="-1">Your ${picks.length} to try</h2>
        <p class="vial-quiz__hint">${
          r.relaxed
            ? 'We widened your price range or dislikes slightly to find enough good matches. '
            : ''
        }Each one starts as a 2ml. Change the size or untick any you don't want.</p>
      </div>
      <ul class="vial-quiz__grid">${picks.map(card).join('')}</ul>
      <div class="vial-quiz__summary">
        <p class="vial-quiz__total" data-summary>${summaryText(total, inSet.length)}</p>
        <button type="button" class="button vial-quiz__add" ${inSet.length ? '' : 'disabled'}>Add my set to cart</button>
        <p class="vial-quiz__msg" data-msg role="status"></p>
        <p class="vial-quiz__links">
          <button type="button" class="vial-quiz__link vial-quiz__restart">Start again</button>
          <a class="vial-quiz__link" href="${FALLBACK_URL}">Browse every fragrance</a>
        </p>
      </div>`;
    bindResults();
    focusQ();
  }

  function refreshSummary() {
    const { inSet, total } = totals();
    app.querySelector('[data-summary]').innerHTML = summaryText(total, inSet.length);
    const add = app.querySelector('.vial-quiz__add');
    add.disabled = !inSet.length;
    add.textContent = 'Add my set to cart';
    app.querySelector('[data-msg]').textContent = '';
  }

  function bindResults() {
    app.querySelectorAll('.vial-quiz__size').forEach((b) =>
      b.addEventListener('click', () => {
        size[b.dataset.h] = Number(b.dataset.id);
        b.closest('.vial-quiz__sizes').querySelectorAll('.vial-quiz__size').forEach((o) => o.setAttribute('aria-pressed', String(o === b)));
        refreshSummary();
      })
    );
    app.querySelectorAll('[data-keep]').forEach((c) =>
      c.addEventListener('change', () => {
        if (c.checked) delete off[c.dataset.keep];
        else off[c.dataset.keep] = true;
        c.closest('.vial-quiz__card').classList.toggle('is-off', !c.checked);
        refreshSummary();
      })
    );
    app.querySelector('.vial-quiz__add').addEventListener('click', addAll);
    app.querySelector('.vial-quiz__restart').addEventListener('click', restart);
  }

  function restart() {
    reset();
    head.hidden = false;
    renderQ();
    focusQ();
  }

  async function addAll() {
    const btn = app.querySelector('.vial-quiz__add');
    const msg = app.querySelector('[data-msg]');
    const { inSet } = totals();
    if (!inSet.length) return;
    const items = inSet.map((x) => ({ id: chosen(x).id, quantity: 1 }));
    btn.disabled = true;
    btn.textContent = 'Adding…';
    msg.textContent = '';

    const routes = (window.Theme && window.Theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart';
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: i.id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    let settled = false;
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const failed = !!res.status;
      settled = true;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'vial-scent-quiz',
          sourceId: 'vial-scent-quiz',
          itemCount: items.length,
          sections: res.sections,
          didError: failed,
        },
      });
      if (failed) throw new Error(res.description || res.message || 'Add to cart failed');
      btn.textContent = 'Added to your cart';
      msg.innerHTML = `Done. <a href="${cartUrl}">View cart</a>`;
    } catch (e) {
      if (!settled) deferred.reject(e);
      btn.disabled = false;
      btn.textContent = 'Add my set to cart';
      msg.textContent = `Something went wrong: ${e.message}. Please try again.`;
    }
  }

}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}
